import type { Location } from "@/types/Location";

/**
 * Resolves the destination for the sidebar "Calendar" quick-link.
 *
 * IMPORTANT: this must be driven by the member's *resolved* permissible locations
 * (id → Location objects, carrying real `slug`s), NOT by `member.permissableLocations`
 * — that array-of-objects field is `null` in production for members who only carry
 * `permissableLocationIds` (the string-id field). Reading the null field sent every
 * such member to the hardcoded `bristol-clifton` fallback, which then 403'd anyone who
 * lacked bristol-clifton access (see John McLane, 2026-08-03).
 *
 * @param permissibleLocations resolved Location objects the member may access
 * @param preferredLocationIds the member's preferred location ids (first wins)
 */
export function resolveCalendarUrl(
  permissibleLocations: Pick<Location, "id" | "slug">[] | null | undefined,
  preferredLocationIds: string[] | null | undefined,
): string {
  const locations = permissibleLocations ?? [];
  const preferred = preferredLocationIds ?? [];

  // No resolved locations (still loading, or a genuinely zero-location member).
  if (locations.length === 0) {
    return "/location/bristol-clifton/calendar";
  }

  // Honour the member's first preferred location when it's one they can actually reach.
  if (preferred.length > 0) {
    const preferredLocation = locations.find((loc) => loc.id === preferred[0]);
    if (preferredLocation?.slug) {
      return `/location/${preferredLocation.slug}/calendar`;
    }
  }

  // Otherwise fall back to the first permissible location.
  const firstLocation = locations[0];
  if (firstLocation?.slug) {
    return `/location/${firstLocation.slug}/calendar`;
  }

  return "/location/bristol-clifton/calendar";
}
